import { BehaviorSubject } from 'rxjs';

import OrderStatusPopup from '../components/orders/OrderStatusPopup';
import Order from '../models/Order';

const defaultCriteria = {
  limit: 10,
  orderField: 'user.createdAt',
  orderType: 'DESC',
  page: 0,
  status: 'demo,registered,paid,complete,cancelled',
};

export default class OrderList {
  orders$ = new BehaviorSubject([]);
  total$ = new BehaviorSubject(0);
  searchCriteria$ = new BehaviorSubject(defaultCriteria);
  searchSubscription;
  constructor({ orderTrackingRepository, dialogRepository, errorRepository }) {
    this.orderTrackingRepository = orderTrackingRepository;
    this.dialogRepository = dialogRepository;
    this.errorRepository = errorRepository;
    this.searchSubscription = this.searchCriteria$.subscribe(() => {
      this.getOrderTracking();
    });
  }

  async getOrderTracking() {
    const orders = await this.orderTrackingRepository.getOrderTracking(this.searchCriteria$.getValue());
    orders.data = orders.data.map((item) => new Order(item));
    this.orders$.next(orders.data);
    this.total$.next(orders.total);
  }

  dispose() {
    this.searchSubscription.unsubscribe();
  }

  updateSearchCriteria(updatedCriteria) {
    const statusField = updatedCriteria.status ? { status: updatedCriteria.status.length ? updatedCriteria.status.join(',') : 'no-order' } : {};

    this.searchCriteria$.next({
      ...this.searchCriteria$.getValue(),
      ...updatedCriteria,
      ...statusField,
    });
  }

  async openOrderStatusPopup(selectedOrder) {
    if (selectedOrder?.status?.value === 'complete') {
      this.errorRepository.error$.next({
        message: "Order completed. Can't perform order status change.",
        severity: 'info',
      });
      return;
    }

    this.dialogRepository.loadDialog(OrderStatusPopup, {
      createdAt: selectedOrder.createdAt,
      hasPaymentDetail: selectedOrder.hasPaymentDetail,
      name: selectedOrder.name,
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onConfirm: (status) => this.handleOrderStatusChange(selectedOrder.id, selectedOrder.status.value, status),
      status: selectedOrder.status.value,
    });
  }

  async handleOrderStatusChange(id, currentStatus, status) {
    this.dialogRepository.toggleDialog(false);
    if (currentStatus === status) return;

    if (status === 'archived') this.putUserToArchivedStatus(id);
    if (status === 'cancelled') this.putUserToCancelledStatus(id);
    if (status === 'paid') this.putUserToPaidStatus(id);
    if (status === 'registered') this.putUserToRegisteredStatus(id);
    if (status === 'complete') this.putUserToCompleteStatus(id);
  }

  async putUserToArchivedStatus(userId) {
    try {
      await this.orderTrackingRepository.putUserToArchivedStatus(userId);
      this.errorRepository.error$.next({
        message: 'Order archived!',
        severity: 'success',
      });
      this.getOrderTracking();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update order status to archived',
        severity: 'error',
      });
    }
  }

  async putUserToCancelledStatus(userId) {
    try {
      await this.orderTrackingRepository.putUserToCancelledStatus(userId);
      this.errorRepository.error$.next({
        message: 'Order cancelled!',
        severity: 'success',
      });
      this.getOrderTracking();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update order status to cancelled',
        severity: 'error',
      });
    }
  }

  async putUserToRegisteredStatus(userId) {
    try {
      await this.orderTrackingRepository.putUserToRegisteredStatus(userId);
      this.errorRepository.error$.next({
        message: 'Updated order status to registered!',
        severity: 'success',
      });
      this.getOrderTracking();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update order status to registered',
        severity: 'error',
      });
    }
  }

  async putUserToPaidStatus(userId) {
    try {
      await this.orderTrackingRepository.putUserToPaidStatus(userId);
      this.errorRepository.error$.next({
        message: 'Updated order status to paid!',
        severity: 'success',
      });
      this.getOrderTracking();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update order status to paid',
        severity: 'error',
      });
    }
  }

  async putUserToCompleteStatus(userId) {
    try {
      await this.orderTrackingRepository.putUserToCompleteStatus(userId);
      this.errorRepository.error$.next({
        message: 'Update order status to completed!',
        severity: 'success',
      });
      this.getOrderTracking();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update order status to completed',
        severity: 'error',
      });
    }
  }
}
